import { useState } from 'react';
import { Loader, Box } from '@coolblue-development/freddy';
import { datadogLogs } from '@datadog/browser-logs';
import { Download, Refresh } from '@coolblue-development/icons';
import { EmailAttachment } from '../../../../../models/Email';
import { fetchAttachment } from '../../../../../services/fetchAttachment';
import BaseAttachmentChip from '../BaseAttachmentChip';

interface ReceivedEmailAttachmentChipsProps {
  attachments: EmailAttachment[];
  contactId: string;
  onDownloadError?: () => void;
}

const ReceivedEmailAttachmentChips = ({
  attachments,
  contactId,
  onDownloadError
}: ReceivedEmailAttachmentChipsProps): JSX.Element | null => {
  const [loadingIds, setLoadingIds] = useState<Set<string>>(new Set());
  const [errorIds, setErrorIds] = useState<Set<string>>(new Set());

  if (attachments.length === 0) return null;

  const clearError = (fileId: string): void =>
    setErrorIds(prev => {
      const next = new Set(prev);
      next.delete(fileId);
      return next;
    });

  const handleDownload = async (attachment: EmailAttachment): Promise<void> => {
    clearError(attachment.fileId);
    setLoadingIds(prev => new Set(prev).add(attachment.fileId));
    try {
      const { url } = await fetchAttachment(attachment.fileId, contactId);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      datadogLogs.logger.error('Failed to download attachment', { channel: 'email' }, error as Error);
      onDownloadError?.();
      setErrorIds(prev => new Set(prev).add(attachment.fileId));
    } finally {
      setLoadingIds(prev => {
        const next = new Set(prev);
        next.delete(attachment.fileId);
        return next;
      });
    }
  };

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '12px', marginBottom: '12px' }}>
      {attachments.map(attachment => {
        const isLoading = loadingIds.has(attachment.fileId);
        const isError = errorIds.has(attachment.fileId);
        return (
          <BaseAttachmentChip
            key={attachment.fileId}
            fileName={attachment.fileName}
            variant={isError ? 'error' : 'default'}
            onClick={isLoading ? undefined : () => void handleDownload(attachment)}
            trailing={
              isLoading ? (
                <Box sx={{ position: 'relative', marginRight: '8px' }}>
                  <Loader size="small" />
                </Box>
              ) : isError ? (
                <Refresh color="#D71F1F" />
              ) : (
                <Download color="#0090E3" />
              )
            }
          />
        );
      })}
    </Box>
  );
};

export default ReceivedEmailAttachmentChips;
